import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, PencilIcon, TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  DangerButton,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  NumberInput,
} from "../../components/crm/Form";
import SubscriptionStatusBadge from "../../components/billing/SubscriptionStatusBadge";
import SubscriptionActionBar from "../../components/billing/SubscriptionActionBar";
import SubscriptionEventTrail from "../../components/billing/SubscriptionEventTrail";
import BillingPanel from "../../components/billing/BillingPanel";
import NotificationsMiniFeed from "../../components/notifications/NotificationsMiniFeed";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const OFFICE = ["owner", "ops"];
const CADENCE_LABEL = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

function fmtCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SubscriptionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isOffice = OFFICE.includes(user?.role);

  const [sub, setSub] = useState(null);
  const [events, setEvents] = useState([]);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editAmount, setEditAmount] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/subscriptions/${id}`);
      setSub(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    if (isOffice) {
      try {
        const { data } = await api.get(`/subscriptions/${id}/events`);
        setEvents(data || []);
      } catch {
        setEvents([]);
      }
    }
  }, [id, isOffice]);
  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    setEditAmount((sub.amount_cents / 100).toFixed(2));
    setEditNotes(sub.notes_internal || "");
    setEditErr("");
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    setEditErr("");
    const body = {};
    const newAmt = Math.round(Number(editAmount) * 100);
    if (newAmt !== sub.amount_cents) body.amount_cents = newAmt;
    if (editNotes !== (sub.notes_internal || "")) body.notes_internal = editNotes;
    if (Object.keys(body).length === 0) {
      setEditOpen(false);
      setEditBusy(false);
      return;
    }
    try {
      const { data } = await api.patch(`/subscriptions/${id}`, body);
      setSub(data);
      setEditOpen(false);
      toast.success("Saved");
      // Refresh events
      const ev = await api.get(`/subscriptions/${id}/events`);
      setEvents(ev.data || []);
    } catch (e2) {
      setEditErr(formatApiError(e2));
    } finally {
      setEditBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Soft-delete this subscription? It cannot be undone."))
      return;
    try {
      await api.delete(`/subscriptions/${id}`);
      toast.success("Subscription deleted");
      nav("/subscriptions");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!sub) {
    return (
      <Layout title="Subscription" subtitle="Loading">
        <ErrorBanner message={err} />
      </Layout>
    );
  }

  const isTechView = !isOffice; // tech-slim payload

  return (
    <Layout title={sub.plan_name} subtitle="Subscription">
      <button
        onClick={() => nav("/subscriptions")}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-amber-700 mb-4"
        data-testid="back-to-subscriptions"
      >
        <ArrowLeftIcon size={12} weight="bold" /> All Subscriptions
      </button>

      <EntityHeader
        overline={sub.plan_code || "subscription"}
        title={sub.plan_name}
        subtitle={
          sub.customer_name
            ? `${sub.customer_name}${sub.property_address ? " · " + sub.property_address : ""}`
            : "Subscription"
        }
        actions={
          isOffice ? (
            <div className="flex gap-2">
              <SecondaryButton onClick={openEdit} data-testid="sub-edit-button">
                <PencilIcon size={12} weight="bold" /> Edit
              </SecondaryButton>
              <DangerButton onClick={remove} data-testid="sub-delete-button">
                <TrashIcon size={12} weight="bold" /> Delete
              </DangerButton>
            </div>
          ) : null
        }
      />

      <ErrorBanner message={err} />

      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-card mt-4 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <SubscriptionStatusBadge status={sub.status} />
          {sub.cancel_at_period_end && (
            <span className="text-xs text-rose-700">
              Will cancel at period end
            </span>
          )}
        </div>
        {isOffice && <SubscriptionActionBar subscription={sub} onChange={(s) => {
          setSub(s);
          api.get(`/subscriptions/${id}/events`).then((r) => setEvents(r.data || []));
        }} />}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <section
            className="rounded-md border border-slate-200 bg-white p-5 shadow-card"
            data-testid="sub-summary-card"
          >
            <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
              Subscription
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Detail label="Plan" value={sub.plan_name} />
              <Detail
                label="Cadence"
                value={CADENCE_LABEL[sub.billing_cadence] || sub.billing_cadence}
              />
              {!isTechView && (
                <Detail
                  label="Amount"
                  value={`${fmtCents(sub.amount_cents)} ${
                    sub.billing_cadence === "monthly"
                      ? "/ month"
                      : sub.billing_cadence === "quarterly"
                        ? "/ quarter"
                        : "/ year"
                  }`}
                  mono
                />
              )}
              {!isTechView && (
                <Detail
                  label="MRR contribution"
                  value={
                    sub.status === "active"
                      ? fmtCents(sub.mrr_contribution_cents)
                      : "—"
                  }
                  mono
                />
              )}
              {!isTechView && (
                <Detail label="Start date" value={sub.start_date} mono />
              )}
              {!isTechView && (
                <Detail
                  label="Current period"
                  value={
                    sub.current_period_start && sub.current_period_end
                      ? `${sub.current_period_start} → ${sub.current_period_end}`
                      : "—"
                  }
                  mono
                />
              )}
            </dl>
          </section>

          {!isTechView && (
            <section
              className="rounded-md border border-slate-200 bg-white p-5 shadow-card"
              data-testid="sub-notes"
            >
              <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
                Internal notes
              </div>
              {sub.notes_internal ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {sub.notes_internal}
                </p>
              ) : (
                <div className="text-xs text-slate-500">
                  No internal notes — use Edit to add context.
                </div>
              )}
            </section>
          )}

          {sub.paused_at && (
            <section
              className="rounded-md border border-amber-200 bg-amber-50 p-4"
              data-testid="sub-paused-card"
            >
              <div className="text-[11px] uppercase tracking-wider text-amber-900 font-bold mb-1">
                Paused
              </div>
              <div className="text-xs text-amber-900">
                {new Date(sub.paused_at).toLocaleString()}
                {sub.paused_reason ? ` · ${sub.paused_reason}` : ""}
              </div>
            </section>
          )}

          {sub.canceled_at && (
            <section
              className="rounded-md border border-red-200 bg-red-50 p-4"
              data-testid="sub-canceled-card"
            >
              <div className="text-[11px] uppercase tracking-wider text-red-800 font-bold mb-1">
                Canceled
              </div>
              <div className="text-xs text-red-800">
                {new Date(sub.canceled_at).toLocaleString()}
                {sub.cancel_reason ? ` · ${sub.cancel_reason}` : ""}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          {isOffice && (
            <BillingPanel subscription={sub} onChange={load} />
          )}
          {isOffice && (
            <NotificationsMiniFeed subscriptionId={sub.id} />
          )}
          {isOffice && (
            <section
              className="rounded-md border border-slate-200 bg-white p-5"
              data-testid="sub-events-card"
            >
              <div className="font-heading text-sm font-semibold text-slate-900">
                Lifecycle history
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
                Append-only audit
              </div>
              <SubscriptionEventTrail entries={events} />
            </section>
          )}

          <section
            className="rounded-md border border-slate-200 bg-white p-5 text-xs text-slate-700 space-y-1.5"
            data-testid="sub-meta"
          >
            <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
              Linked records
            </div>
            <div>
              <span className="text-slate-500">Customer:</span>{" "}
              <Link
                to={`/customers/${sub.customer_id}`}
                className="text-slate-900 hover:text-amber-700 font-medium"
              >
                {sub.customer_name || sub.customer_id?.slice(0, 8)}
              </Link>
            </div>
            {sub.property_id && (
              <div>
                <span className="text-slate-500">Property:</span>{" "}
                <Link
                  to={`/properties/${sub.property_id}`}
                  className="text-slate-900 hover:text-amber-700 font-medium"
                >
                  {sub.property_address || sub.property_id?.slice(0, 8)}
                </Link>
              </div>
            )}
            {sub.solar_system_id && (
              <div>
                <span className="text-slate-500">Solar system:</span>{" "}
                <Link
                  to={`/solar-systems/${sub.solar_system_id}`}
                  className="text-slate-900 hover:text-amber-700 font-medium"
                >
                  {sub.solar_system_id.slice(0, 8)}
                </Link>
              </div>
            )}
          </section>
        </aside>
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit subscription"
        subtitle="Adjust price or internal notes — appends an audit event"
        data-testid="sub-edit-modal"
      >
        <form onSubmit={submitEdit} className="space-y-4">
          <ErrorBanner message={editErr} />
          <Field label="Amount (USD)">
            <NumberInput
              value={editAmount}
              onChange={setEditAmount}
              min={0}
              step={0.01}
              data-testid="sub-edit-amount"
            />
          </Field>
          <Field label="Internal notes">
            <TextArea
              value={editNotes}
              onChange={setEditNotes}
              rows={4}
              data-testid="sub-edit-notes"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setEditOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={editBusy}
              data-testid="sub-edit-submit"
            >
              {editBusy ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </dt>
      <dd
        className={`text-slate-900 mt-0.5 ${mono ? "font-data" : "font-medium"}`}
      >
        {value}
      </dd>
    </div>
  );
}
